import { 
  Box, 
  Image, 
  // Input,
  Text,
  Textarea,
  Button,
  // Checkbox,
  // Grid,
  // GridItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Menu,
  MenuButton,
  MenuList,
  // MenuItem,
  defineStyle, 
  defineStyleConfig,
  extendTheme,
} from "@chakra-ui/react";
import { mode } from '@chakra-ui/theme-tools';
import type { StyleFunctionProps } from '@chakra-ui/styled-system';
import { 
  IScreen, ICustomMenu, ICustomPopup 
} from "./Interfaces";
import { truncate } from "./Util";
import { useContext } from "react";
import { ScreenContext } from "./ScreenProvider";
import arrowUp from "../assets/icons/icon-arrow-up.svg";
import arrowDown from "../assets/icons/icon-arrow-down.svg";
// import styled from "styled-components";

const textareaStyle = defineStyle({
  background: '#FCFCFC',
  color: '#969696',
  // fontFamily: 'serif',
  // fontWeight: 'normal',

  // let's also provide dark mode alternatives
  _dark: {
    background: '#FCFCFC',
    color: '#969696',
  }
})

export const textareaTheme = defineStyleConfig({
  variants: { textareaStyle },
})

export const theme = extendTheme({
  styles: {
    global: (props: StyleFunctionProps) => ({
      body: {
        fontFamily: 'TbellFont',
        color: mode('white', 'whiteAlpha.900')(props),
        bg: mode('#121212', 'gray.800')(props),
        lineHeight: 'base',
      },
    }),
  },
  colors: {
    tRed: {
      100: "#FF4343",
      500: "#FF4343",
      600: "#FF4343",
      900: "#F02929",
    },
    tGreen: {
      100: "#27C4D5",
      200: "#10AACC",
      300: "#0788A4",
      500: "#2EA090",
      600: "#2EA090",
      900: "#2EA090",
    },
    tBlue: {
      100: "#5498FF",
      500: "#5498FF",   //color scheme
      600: "#5498FF",   //Text
      900: "#0066FF",
    },
    tYellow: {
      100: "#FAE100",
      500: "#FAE100",
      600: "#FAE100",
      900: "#FAE100",
    },
    tGrey: {
      100: "#5F6164",
      500: "#5F6164",
      600: "#5F6164",
      900: "#595959",
    },
    tBlack: {
      100: "#121212",
      300: "#121212",
      500: "#121212",
      600: "#121212",
      900: "#121212",
    },
    tPink: {
      100: "#F24B91",
      300: "#F24B91",
      500: "#F24B91",
      600: "#F24B91",
      900: "#F24B91",
    }
  },
  components: {
    Link: {
      baseStyle: {
          _focus: {
              boxShadow: 'none'
          }
      }
    },
    Card: {
      baseStyle: {
        _focus: {
            boxShadow: 'none'
        }
      }
    },
    PopoverArrow: {
      baseStyle: {
        _focus: {
            boxShadow: 'none'
        }
      }
    },
    Checkbox: {
      baseStyle: {
        _focus: {
          boxShadow: 'none'
        },
        _focusVisible: {
          boxShadow: 'none'
        },
        _dataFocusVisible: {
          boxShadow: 'none'
        },
      }
    },
    Textarea: textareaTheme,
  },
});

const SelectButton = ({children, width="auto", height="auto", weight=400, isSelected, onClick, value}) => {
  return (
    <Button 
      width={width}
      height={height}
      minH={"40px"}
      colorScheme={"tRed"} 
      border={"0.5px solid #FF4343"}
      variant={isSelected ? "solid" : "outline"}
      fontSize={"clamp(12px, 1.2vw, 17px)"}
      fontWeight={weight}
      onClick={onClick}
      value={value}
    >
      {children}
    </Button>
  )
};

const SubmitButton = ({children, onClick, width="100%", height="50px"}) => {
  return (
    <Button
      w={width}
      h={"50px"}
      colorScheme={"tRed"}
      color={"white"}
      borderRadius={"10px"}
      padding={"10px"}
      fontWeight={700}
      _hover={{ background: "#FF7272" }}
      onClick={onClick}
    >
      {children}
    </Button>
  )
};

const SubButton = ({children, onClick, ml="0", mr="0", width="fit-content", height="40px"}) => {
  return (
    <Button
      w={width}
      h={height}
      colorScheme={"tGrey"}
      ml={ml}
      mr={mr}
      onClick={onClick}
    >
      {children}
    </Button>
  )
};

// const MenuText = ({children}) => {

// };

const ResponsiveText = ({
  children,
  color="#ffffff",
  size,
  weight="400",
}) => {
  return (
    <Text
      color={color}
      fontSize={size}
      fontWeight={weight}
      whiteSpace={"pre-wrap"}
    >
      {children}
    </Text>
  )
};

const ResponsiveTextArea = ({
  width="100%", 
  height, 
  fontSize, 
  resize, 
  isReadOnly=false, 
  value,
  border="none",
}) => {
  return (
    <Textarea 
      width={width}
      height={height}
      fontSize={fontSize}
      resize={resize}
      value={value}
      isReadOnly={isReadOnly}
      variant={"textareaStyle"}
      border={border}
      overflowY={"auto"}
      css={{
        '&::-webkit-scrollbar': {
          width: '10px'
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#A4A8AD',
          borderRadius: '10px'
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
          width: '15px'
        }
      }}
    />
  )
}

const ResponsiveMenu: React.FC<ICustomMenu> = ({
  isMenuOpen,
  type,
  shownText = "Select...",
  width = "20vw",
  bgColor = { button: "white", list: "white" },
  borderColor = { button: "grey", list: "grey" },
  color = { button: "black", list: "black" },
  radius,
  fontSize = { button: "20px", list: "20px" },
  children,
  onClick,
}) => {
  const screen: IScreen = useContext(ScreenContext);
  return (
    <Menu gutter={0} isOpen={isMenuOpen}>
      {/* {({ isOpen }) => ( */}
        <>
          <MenuButton
            display={"flex"}
            flexDirection={"row"}
            alignItems={"center"}
            bgColor={bgColor.button}
            border={"2px"}
            borderColor={borderColor.button}
            borderRadius={radius ? radius : isMenuOpen ? "5px 5px 0 0" : "5px"}
            /* outline={`2px solid ${borderColor}`} */
            color={color.button}
            width={type === "project" ? `calc(${width} + ${shownText.length/15}vw)` : width}
            maxWidth={"90vw"}
            h={"80%"}
            minH={"36px"}
            _focus={{ bgColor: bgColor.button, borderColor: borderColor.button }}
            _hover={{ bgColor: bgColor.button, borderColor: borderColor.button }}
            _expanded={{ bgColor: bgColor.button, borderColor: borderColor.button }}
            /* _focusWithin={{ bgColor: bgColor.button, borderColor: borderColor.button }} */
            isActive={isMenuOpen}
            onClick={onClick}
            as={Button}
            rightIcon={<Image w={"1.3vw"} minW={"10px"} src={isMenuOpen ? arrowUp : arrowDown} />}
            fontSize={fontSize.button}
            p={"0 1vw"}
          >
            <Text>{screen.style === "mobile" ? truncate(shownText, 7) : truncate(shownText, 15)}</Text>
          </MenuButton>
          <MenuList
            mt={0}
            width={type === "project" ? `calc(${width} + ${shownText.length/15}vw)` : width}
            minW={"max-content"}
            maxH={"50vh"}
            overflowY={"auto"}
            color={color.list}
            bgColor={bgColor.list}
            border={"2px"}
            borderColor={borderColor.list}
            borderRadius={radius ? radius : isMenuOpen ? "0 0 5px 5px" : "none"}
            /* outline={`2px solid ${borderColor}`} */
          >
            {children}
          </MenuList>
        </>
      {/* )} */}
    </Menu>
  );
};

const CustomPopup: React.FC<ICustomPopup> = ({
  children,
  isOpen,
  onClose,
  handleSubmit,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader></ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {children}
        </ModalBody>
        <ModalFooter>
          <Box w={"100%"} display={"flex"} justifyContent={"end"}>
            <SubButton onClick={onClose} mr={"10px"}>
              {handleSubmit ? "취소" : "닫기"}
            </SubButton>
            {handleSubmit &&
              <SubmitButton onClick={handleSubmit}>
                확인
              </SubmitButton>
            }
          </Box>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export { 
  SelectButton, 
  SubmitButton, 
  SubButton, 
  ResponsiveText,
  ResponsiveTextArea, 
  ResponsiveMenu,
  CustomPopup,
};